import Sortable from 'sortablejs/modular/sortable.core.esm.js';
import {AppConfig} from "../../AppConfig";
import {AuthorityProvider} from "../../lib/ContainerActionRules";
import {createHtmlElement, toggleHidden} from "../../lib/domFunctions";
import {escapeHTML} from "../../lib/escapeHTML";
import {Messages} from "../../messages/Messages";
import {
	DashboardWidget,
	DashboardWidgetContext,
	DashboardWidgetDescriptor,
	DashboardWidgetHostApi
} from "./DashboardWidget";
import {DashboardLayoutStore, DashboardWidgetConfig} from "./DashboardLayoutStore";
import {WidgetRegistry} from "./WidgetRegistry";

const messages = new Messages();

export class WidgetHost implements DashboardWidgetHostApi {
	readonly element: HTMLElement

	private readonly registry: WidgetRegistry
	private readonly store: DashboardLayoutStore
	private readonly context: DashboardWidgetContext
	private readonly emptyNotice: HTMLElement

	private config: DashboardWidgetConfig[] = []
	private descriptors: DashboardWidgetDescriptor[] = []
	private instances: DashboardWidget[] = []
	private sortable: Sortable | null = null
	private rowsAvailable: 1 | 2 = 1
	private readonly cards = new Map<string, HTMLElement>()

	constructor(element: HTMLElement, registry: WidgetRegistry, store: DashboardLayoutStore, context: DashboardWidgetContext) {
		this.element = element
		this.registry = registry
		this.store = store
		this.context = context
		this.context.host = this

		this.emptyNotice = createHtmlElement('div', {class: 'text-muted fst-italic text-center p-3 hidden'},
			escapeHTML(messages.get('dashboard.widgets.allHidden')))
		this.element.append(this.emptyNotice)

		this.rebuild()
	}

	get availableDescriptors(): DashboardWidgetDescriptor[] {
		return this.descriptors
	}

	get layoutConfig(): DashboardWidgetConfig[] {
		return this.config
	}

	rebuild() {
		this.sortable?.destroy()
		this.sortable = null
		this.instances.forEach(i => i.dispose())
		this.instances = []
		this.cards.clear()
		Array.from(this.element.querySelectorAll('.dashboard-widget')).forEach(n => n.remove())

		this.descriptors = this.registry.available(new AuthorityProvider())
		this.config = this.store.load(this.descriptors, AppConfig.roles)
		this.normalizePositions()

		this.config
			.filter(c => c.visible)
			.forEach(c => {
				const descriptor = this.descriptors.find(d => d.id == c.id)
				if (descriptor) this.mount(descriptor)
			})

		toggleHidden(this.emptyNotice, this.instances.length > 0)

		this.sortable = Sortable.create(this.element, {
			handle: '.drag-handle',
			draggable: '.dashboard-widget',
			onEnd: evt => this.onDragEnd(evt)
		})

		this.relayout()
		this.resetWidgets()
	}

	showWidget(id: string) {
		this.setWidgetVisible(id, true)
	}

	setWidgetVisible(id: string, visible: boolean) {
		const entry = this.config.find(c => c.id == id)
		if (!entry || entry.visible == visible) return

		entry.visible = visible
		this.store.save(this.config)
		this.rebuild()
	}

	applyConfig(visibleById: Map<string, boolean>) {
		this.config.forEach(c => {
			const visible = visibleById.get(c.id)
			if (typeof visible === 'boolean') c.visible = visible
		})
		this.store.save(this.config)
		this.rebuild()
	}

	resetLayout() {
		this.store.reset()
		this.rebuild()
	}

	resetWidgets() {
		this.instances.forEach(i => i.reset?.())
	}

	setRowsAvailable(rows: 1 | 2) {
		if (this.rowsAvailable == rows) return

		this.rowsAvailable = rows
		this.relayout()
	}

	toggleSpan(id: string) {
		const entry = this.config.find(c => c.id == id)
		if (!entry) return

		if (entry.span == 2) {
			delete entry.span
		} else {
			entry.span = 2
		}
		this.store.save(this.config)
		this.relayout()
	}

	private mount(descriptor: DashboardWidgetDescriptor) {
		const card = createHtmlElement('div', {class: 'dashboard-widget card', 'data-widget-id': descriptor.id}, `
<div class="card-header d-flex align-items-center py-1 px-2">
	<i class="bi bi-grip-vertical drag-handle me-1 text-muted"></i>
	<span class="flex-fill">${escapeHTML(messages.get(descriptor.titleCode))}</span>
	<button type="button" class="dashboard-widget__action text-muted bg-transparent border-0 p-0 me-2" data-bind="toggleSpan"></button>
	<button type="button" class="btn-close" data-bind="hide" title="${escapeHTML(messages.get('dashboard.widgets.hide'))}"></button>
</div>
<div class="dashboard-widget__content"></div>
		`)
		const hideButton = card.querySelector<HTMLElement>('[data-bind="hide"]')!
		hideButton.addEventListener('click', () => this.setWidgetVisible(descriptor.id, false))
		const spanButton = card.querySelector<HTMLElement>('[data-bind="toggleSpan"]')!
		spanButton.addEventListener('click', () => this.toggleSpan(descriptor.id))

		this.element.append(card)
		this.cards.set(descriptor.id, card)

		const content = card.querySelector<HTMLElement>('.dashboard-widget__content')!
		const instance = descriptor.create(this.context, content)
		instance.headerActions?.forEach(action => spanButton.before(action))
		this.instances.push(instance)
	}

	private cols(): number {
		return window.innerWidth >= 2100 ? 6 : window.innerWidth >= 1500 ? 5 : 4
	}

	private effectiveSpan(entry: DashboardWidgetConfig): number {
		return entry.span == 2 && this.rowsAvailable == 2 ? 2 : 1
	}

	private cellsOf(entry: DashboardWidgetConfig, pos: number | undefined = entry.pos): number[] {
		if (pos == null) return []
		const cells = [pos]
		if (this.effectiveSpan(entry) == 2) cells.push(pos + this.cols())
		return cells
	}

	private firstFreeCell(taken: Set<number>, span: number): number {
		const cols = this.cols()
		for (let cell = 0; ; cell++) {
			let free = true
			for (let i = 0; i < span; i++) {
				if (taken.has(cell + i * cols)) {
					free = false
					break
				}
			}
			if (free) return cell
		}
	}

	private normalizePositions() {
		const visible = this.config.filter(c => c.visible)
		const taken = new Set<number>()

		visible.forEach(c => {
			const cells = this.cellsOf(c)
			if (c.pos != null && cells.length > 0 && cells.every(cell => cell >= 0 && !taken.has(cell))) {
				cells.forEach(cell => taken.add(cell))
			} else {
				delete c.pos
			}
		})
		visible.forEach(c => {
			if (c.pos == null) {
				c.pos = this.firstFreeCell(taken, this.effectiveSpan(c))
				this.cellsOf(c).forEach(cell => taken.add(cell))
			}
		})
	}

	private layoutCards() {
		const cols = this.cols()
		const visible = this.config
			.filter(c => c.visible && this.cards.has(c.id) && c.pos != null)
			.sort((a, b) => a.pos! - b.pos!)

		visible.forEach(c => {
			const card = this.cards.get(c.id)!
			this.element.append(card)
			card.style.gridColumnStart = `${c.pos! % cols + 1}`
			card.style.gridRowStart = `${Math.floor(c.pos! / cols) + 1}`
			card.style.gridRowEnd = this.effectiveSpan(c) == 2 ? 'span 2' : ''
		})
	}

	private updateSpanButtons() {
		this.cards.forEach((card, id) => {
			const entry = this.config.find(c => c.id == id)
			const span2 = entry != null && this.effectiveSpan(entry) == 2
			card.querySelectorAll<HTMLElement>('[data-bind="toggleSpan"]').forEach(button => {
				toggleHidden(button, this.rowsAvailable != 2)
				button.innerHTML = `<i class="bi ${span2 ? 'bi-arrows-collapse' : 'bi-arrows-expand'}"></i>`
				button.title = messages.get(span2 ? 'dashboard.widgets.collapse' : 'dashboard.widgets.expand')
			})
		})
	}

	private relayout() {
		this.normalizePositions()
		this.layoutCards()
		this.updateSpanButtons()
	}

	private onDragEnd(evt: Sortable.SortableEvent) {
		const id = evt.item.getAttribute('data-widget-id')
		const cell = this.dropCellFromEvent(evt)
		if (id == null || cell == null) {
			this.layoutCards()
			return
		}
		this.moveWidgetToCell(id, cell)
	}

	private dropCellFromEvent(evt: Sortable.SortableEvent): number | null {
		const oe = (evt as any).originalEvent as any
		const point = oe?.touches?.[0] ?? oe?.changedTouches?.[0] ?? oe
		const x = point?.clientX, y = point?.clientY
		if (typeof x != 'number' || typeof y != 'number') return null

		const rect = this.element.getBoundingClientRect()
		if (x < rect.left || x > rect.right) return null

		const styles = getComputedStyle(this.element)
		const gap = parseFloat(styles.gap) || 0
		const padL = parseFloat(styles.paddingLeft) || 0
		const padR = parseFloat(styles.paddingRight) || 0
		const padT = parseFloat(styles.paddingTop) || 0

		const cols = this.cols()
		const colWidth = (rect.width - padL - padR - (cols - 1) * gap) / cols
		if (colWidth <= 0) return null

		const cellHeight = this.cellHeightPx(styles, gap)
		if (cellHeight <= 0) return null

		const col = Math.min(cols - 1, Math.max(0, Math.floor((x - rect.left - padL) / (colWidth + gap))))
		const row = Math.max(0, Math.floor((y - rect.top - padT + this.element.scrollTop) / (cellHeight + gap)))
		return row * cols + col
	}

	private cellHeightPx(styles: CSSStyleDeclaration, gap: number): number {
		const fromVar = parseFloat(this.element.style.getPropertyValue('--widget-cell-h'))
		if (Number.isFinite(fromVar) && fromVar > 0) return fromVar

		const height = this.element.clientHeight
		if (height <= 0) return 0
		return (height - (this.rowsAvailable - 1) * gap) / this.rowsAvailable
	}

	moveWidgetToCell(id: string, targetCell: number) {
		const entry = this.config.find(c => c.id == id)
		if (!entry || targetCell < 0) return

		const newPos = Math.floor(targetCell)
		if (entry.pos == newPos) {
			this.layoutCards()
			return
		}

		const oldPos = entry.pos
		entry.pos = newPos
		const draggedCells = this.cellsOf(entry)

		const evicted = this.config.filter(c =>
			c.visible && c.id != id && c.pos != null &&
			this.cellsOf(c).some(cell => draggedCells.indexOf(cell) >= 0))

		const taken = new Set<number>(draggedCells)
		this.config
			.filter(c => c.visible && c.id != id && evicted.indexOf(c) < 0)
			.forEach(c => this.cellsOf(c).forEach(cell => taken.add(cell)))

		evicted.forEach(c => {
			const spanC = this.effectiveSpan(c)
			const fitsAtOld = oldPos != null && this.cellsOf(c, oldPos).every(cell => !taken.has(cell))
			c.pos = fitsAtOld ? oldPos : this.firstFreeCell(taken, spanC)
			this.cellsOf(c).forEach(cell => taken.add(cell))
		})

		this.store.save(this.config)
		this.layoutCards()
	}
}
